import React, { useContext, useEffect, useState } from "react";
import {
  FaUserGraduate,
  FaChalkboardTeacher,
  FaRegQuestionCircle,
  FaRegImage,
  FaSchool,
  FaBook,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
  FaEye,
  FaClock,
  FaChartLine,
  FaTrophy,
  FaExclamationTriangle,
  FaMusic,
  FaVideo,
  FaFire,
  FaThumbsDown
} from "react-icons/fa";
import { MdOutlineAudiotrack, MdTopic, MdBarChart, MdTimelapse, MdPermMedia, MdTrendingUp } from "react-icons/md";
import { GoVideo } from "react-icons/go";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Label
} from "recharts";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContextProvider";
import APIServicenew from "../../utils/APIGeneralService";
import "./Dashboard.css";

const Dashboard = () => {
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const authToken = process.env.REACT_APP_authToken;
  const { accessToken, data, logout } = useContext(UserContext);

  const headers = { "auth-token": authToken, authorization: accessToken };
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [userType, setUserType] = useState("admin");
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (data?.user_type) {
      setUserType(data.user_type === "school" ? "school" : "admin");
    }
  }, [data?.user_type]);

  useEffect(() => { fetchDashboardData(); }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await APIServices.get(`/admin/dashboard/summary`, headers);
      if (response?.data?.status === true) {
        setDashboardData(response?.data?.data);
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      } else {
        setDashboardData(null);
      }
    } catch (error) {
      console.error("Error fetching dashboard:", error);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="db-root">
        <div className="db-loading">
          <div className="db-spinner" />
          <p>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="db-root">
        <div className="db-empty">
          <div className="db-empty-icon"><FaChartLine /></div>
          <p>No dashboard data available</p>
        </div>
      </div>
    );
  }

  const {
    summary,
    top_courses,
    learning_analytics,
    quiz_analytics,
    student_performance_metrics,
    course_performance_metrics
  } = dashboardData;

  const isAdmin  = userType === "admin" || userType === "super_admin";
  const isSchool = userType === "school";

  const PASS_COLOR    = "#00d26a";
  const FAIL_COLOR    = "#ba1a1a";
  const ATTEMPT_COLORS = ["#006a60", "#343d96", "#f96b3f", "#6f42c1"];

  const monthlyData = learning_analytics?.monthly_trends || [];

  const accuracyData = quiz_analytics?.overall_accuracy ? [
    { name: "Passed", value: quiz_analytics.overall_accuracy.lessons_passed || 0 },
    { name: "Failed", value: quiz_analytics.overall_accuracy.lessons_failed || 0 }
  ] : [];

  const attemptPassCounts = summary?.attempt_pass_counts || {};
  const attemptPassData = [
    { name: "1st",  value: attemptPassCounts.first_attempt_pass  || 0, color: ATTEMPT_COLORS[0] },
    { name: "2nd",  value: attemptPassCounts.second_attempt_pass || 0, color: ATTEMPT_COLORS[1] },
    { name: "3rd",  value: attemptPassCounts.third_attempt_pass  || 0, color: ATTEMPT_COLORS[2] },
    { name: "4th+", value: attemptPassCounts.fourth_attempt_pass || 0, color: ATTEMPT_COLORS[3] }
  ];

  const totalPassed = attemptPassCounts.total_passed || 0;
  const totalFailed = attemptPassCounts.total_failed || 0;

  const formatTime = (seconds) => {
    if (!seconds) return "0s";
    const hrs  = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    let result = "";
    if (hrs  > 0) result += `${hrs}h `;
    if (mins > 0) result += `${mins}m `;
    if (secs > 0 || result === "") result += `${secs}s`;
    return result.trim();
  };

  const getWelcomeMessage = () => {
    if (isAdmin)  return `Welcome Back, ${data?.fullname || "Admin"}!`;
    if (isSchool) return `Welcome Back, ${data?.fullname || "School Admin"}!`;
    return "Welcome Back!";
  };

  const successRate = (() => {
    const p = accuracyData[0]?.value || 0;
    const f = accuracyData[1]?.value || 0;
    return p + f > 0 ? Math.round((p / (p + f)) * 100) : 0;
  })();

  const hasMonthlyData = monthlyData.some(d => d.total_quiz_attempts > 0);

  const smartYAxis = (maxVal) => {
    const ceil = Math.ceil(maxVal);
    if (ceil <= 5) return { allowDecimals: false, domain: [0, 5], ticks: [0, 1, 2, 3, 4, 5] };
    return { allowDecimals: false };
  };

  const maxAttemptVal = Math.max(...attemptPassData.map(d => d.value), 0);
  const maxMonthlyVal = Math.max(
    ...monthlyData.map(d => Math.max(d.total_quiz_attempts || 0, d.total_passed || 0, d.total_failed || 0)),
    0
  );

  const DonutCenterLabel = ({ viewBox }) => {
    const { cx, cy } = viewBox;
    return (
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
        <tspan x={cx} y={cy - 10} fontSize="32" fontWeight="700" fontFamily="Manrope, sans-serif" fill={PASS_COLOR}>
          {successRate}%
        </tspan>
        <tspan x={cx} y={cy + 18} fontSize="11" fontWeight="500" fontFamily="Inter, sans-serif" fill="#767683" letterSpacing="0.05em">
          SUCCESS RATE
        </tspan>
      </text>
    );
  };

  const LineTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const map = {
      total_quiz_attempts: "Total Attempts",
      total_passed: "Passed",
      total_failed: "Failed"
    };
    return (
      <div style={{ background:"#fff", padding:"12px 16px", border:"1px solid #ddd", borderRadius:8, boxShadow:"0 2px 8px rgba(0,0,0,0.12)", fontFamily:"Inter,sans-serif", fontSize:13 }}>
        <div style={{ fontWeight:600, marginBottom:8, color:"#0d1c2e" }}>{label}</div>
        {payload.map((e, i) => (
          <div key={i} style={{ display:"flex", alignItems:"center", marginBottom:4 }}>
            <span style={{ width:10, height:10, borderRadius:"50%", background:e.color, marginRight:8, flexShrink:0 }} />
            <span style={{ marginRight:6, color:"#454652" }}>{map[e.dataKey] || e.dataKey}:</span>
            <strong style={{ color:e.color }}>{e.value}</strong>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="db-root">

      {/* ── Greeting ── */}
      <div className="db-greeting">
        <h2>{getWelcomeMessage()}</h2>
        <p>Here's what's happening with your platform today.</p>
      </div>

      {/* ── KPI Cards ── */}
      <div className="db-kpi-grid">

        {isAdmin && (
          <div className="db-kpi-card" onClick={() => navigate("/admin/school")}>
            <div>
              <p className="db-kpi-label">Total Schools</p>
              <h3 className="db-kpi-value">{summary?.total_schools || 0}</h3>
            </div>
            <div className="db-kpi-icon navy">
              <FaSchool size={26} />
            </div>
          </div>
        )}

        <div className="db-kpi-card" onClick={() => navigate("/admin/course")}>
          <div>
            <p className="db-kpi-label">Total Courses</p>
            <h3 className="db-kpi-value">{summary?.courses?.total || 0}</h3>
            <div>
              {(summary?.courses?.published > 0) && (
                <span className="db-kpi-badge">Published: {summary.courses.published}</span>
              )}
              {(summary?.courses?.draft > 0) && (
                <span className="db-kpi-badge warn">Draft: {summary.courses.draft}</span>
              )}
            </div>
          </div>
          <div className="db-kpi-icon teal">
            <FaBook size={26} />
          </div>
        </div>

        <div className="db-kpi-card" onClick={() => navigate("/admin/student")}>
          <div>
            <p className="db-kpi-label">Total Students</p>
            <h3 className="db-kpi-value">{summary?.total_students || 0}</h3>
          </div>
          <div className="db-kpi-icon coral">
            <FaUsers size={26} />
          </div>
        </div>

        <div className="db-kpi-card">
          <div>
            <p className="db-kpi-label">Total Lessons</p>
            <h3 className="db-kpi-value">{summary?.lessons?.total || 0}</h3>
            <div>
              {(summary?.lessons?.approved > 0) && (
                <span className="db-kpi-badge">Approved: {summary.lessons.approved}</span>
              )}
              {(summary?.lessons?.pending > 0) && (
                <span className="db-kpi-badge warn">Pending: {summary.lessons.pending}</span>
              )}
            </div>
          </div>
          <div className="db-kpi-icon slate">
            <FaChalkboardTeacher size={26} />
          </div>
        </div>

        <div className="db-kpi-card">
          <div>
            <p className="db-kpi-label">Total Topics</p>
            <h3 className="db-kpi-value">{summary?.topics?.total || 0}</h3>
            <div>
              {(summary?.topics?.published > 0) && (
                <span className="db-kpi-badge">Published: {summary.topics.published}</span>
              )}
              {(summary?.topics?.draft > 0) && (
                <span className="db-kpi-badge warn">Draft: {summary.topics.draft}</span>
              )}
            </div>
          </div>
          <div className="db-kpi-icon navy">
            <MdTopic size={26} />
          </div>
        </div>

        {isAdmin && (
          <div className="db-kpi-card">
            <div>
              <p className="db-kpi-label">Images Uploaded</p>
              <h3 className="db-kpi-value">{summary?.media?.images || 0}</h3>
            </div>
            <div className="db-kpi-icon navy">
              <FaRegImage size={26} />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="db-kpi-card">
            <div>
              <p className="db-kpi-label">Audio Files</p>
              <h3 className="db-kpi-value">{summary?.media?.audio || 0}</h3>
            </div>
            <div className="db-kpi-icon teal">
              <MdOutlineAudiotrack size={28} />
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="db-kpi-card">
            <div>
              <p className="db-kpi-label">Video Lessons</p>
              <h3 className="db-kpi-value">{summary?.media?.video || 0}</h3>
            </div>
            <div className="db-kpi-icon coral">
              <GoVideo size={26} />
            </div>
          </div>
        )}

      </div>

      {/* ── Tabs ── */}
      <div className="db-tabs-nav">
        <button className={`db-tab-btn${activeTab === "overview" ? " active" : ""}`} onClick={() => setActiveTab("overview")}>
          Overview
        </button>
        <button className={`db-tab-btn${activeTab === "analytics" ? " active" : ""}`} onClick={() => setActiveTab("analytics")}>
          Learning Analytics
        </button>
        {isSchool && student_performance_metrics && (
          <button className={`db-tab-btn${activeTab === "students" ? " active" : ""}`} onClick={() => setActiveTab("students")}>
            Student Performance
          </button>
        )}
        <button className={`db-tab-btn${activeTab === "course-performance" ? " active" : ""}`} onClick={() => setActiveTab("course-performance")}>
          Course Performance
        </button>
      </div>

      {/* ════════════════════════════════════════════════
          TAB: OVERVIEW
      ════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <>
          {/* Most Popular Courses — full width */}
          {top_courses && top_courses.length > 0 && (
            <div className="db-card db-mb">
              <div className="db-card-header">
                <h3 className="db-card-title">
                  <MdTrendingUp style={{ color:"#006a60" }} />
                  Most Popular Courses
                </h3>
                <button className="db-view-all" onClick={() => navigate("/admin/course")}>View All</button>
              </div>
              <div className="db-card-body p0">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Course</th>
                      <th>Course ID</th>
                      <th>No. of Grades</th>
                      <th style={{ textAlign:"center" }}>Students Enrolled</th>
                      <th style={{ textAlign:"right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_courses.slice(0, 5).map((course, index) => (
                      <tr key={course.id}>
                        <td style={{ color:"#767683", fontWeight:500 }}>{index + 1}</td>
                        <td>
                          <div className="db-course-cell">
                            {course.image
                              ? <img src={course.image} alt={course.title} className="db-course-thumb" />
                              : <div className="db-course-thumb-ph"><FaBook size={16} /></div>
                            }
                            <strong>{course.title}</strong>
                          </div>
                        </td>
                        <td style={{ color:"#454652" }}>{course.unique_course_id}</td>
                        <td>{course.grade_names?.length || 0}</td>
                        <td style={{ textAlign:"center" }}>
                          <span className="db-pill teal">
                            <FaUsers size={11} /> {course.total_students}
                          </span>
                        </td>
                        <td style={{ textAlign:"right" }}>
                          <button className="db-btn-outline" onClick={() => navigate(`/admin/course-performance/${course.id}`)}>
                            <FaEye size={13} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Monthly Performance Trends — full width, only when data exists */}
          {hasMonthlyData ? (
            <div className="db-card db-mb">
              <div className="db-card-header">
                <h3 className="db-card-title">
                  <MdBarChart style={{ color:"#000666" }} />
                  Monthly Performance Trends
                </h3>
              </div>
              <div className="db-card-body">
                <div style={{ height:400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e6eeff" />
                      <XAxis dataKey="month" tick={{ fontSize:12, fill:"#767683" }} />
                      <YAxis tick={{ fontSize:12, fill:"#767683" }} {...smartYAxis(maxMonthlyVal)} />
                      <Tooltip content={<LineTooltip />} />
                      <Legend
                        formatter={(value) => {
                          if (value === "total_quiz_attempts") return "Total Attempts";
                          if (value === "total_passed") return "Passed";
                          if (value === "total_failed") return "Failed";
                          return value;
                        }}
                      />
                      <Line type="monotone" dataKey="total_quiz_attempts" stroke="#4c56af" strokeWidth={2} dot={{ r:4, fill:"#4c56af" }} activeDot={{ r:6 }} name="total_quiz_attempts" />
                      <Line type="monotone" dataKey="total_passed" stroke="#006a60" strokeWidth={2} dot={{ r:4, fill:"#006a60" }} activeDot={{ r:6 }} name="total_passed" />
                      <Line type="monotone" dataKey="total_failed" stroke="#ba1a1a" strokeWidth={2} dot={{ r:4, fill:"#ba1a1a" }} activeDot={{ r:6 }} name="total_failed" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="db-card db-mb">
              <div className="db-card-body">
                <div className="db-empty">
                  <div className="db-empty-icon"><FaChartLine /></div>
                  <p>Monthly performance data is not available yet.</p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════
          TAB: LEARNING ANALYTICS
      ════════════════════════════════════════════════ */}
      {activeTab === "analytics" && (
        <>
          <div className="db-analytics-grid db-mb">

            {/* Quiz Summary — donut */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title"><MdBarChart style={{ color:"#006a60" }} /> Quiz Summary</h3>
                {/* <span style={{ fontSize:11, fontWeight:500, color:"#767683", letterSpacing:"0.05em", textTransform:"uppercase" }}>
                  Academic Year 2024
                </span> */}
              </div>
              <div className="db-card-body">
                {(accuracyData[0]?.value > 0 || accuracyData[1]?.value > 0) ? (
                  <div className="db-quiz-wrap">
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={accuracyData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={110}
                          startAngle={90}
                          endAngle={-270}
                          dataKey="value"
                          paddingAngle={2}
                        >
                          <Cell fill={PASS_COLOR} />
                          <Cell fill={FAIL_COLOR} />
                          <Label content={<DonutCenterLabel />} position="center" />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="db-quiz-legend">
                      <div className="db-quiz-legend-item" style={{ color: PASS_COLOR }}>
                        <span style={{ width:12, height:12, borderRadius:"50%", background: PASS_COLOR, flexShrink:0 }} />
                        Passed: {((accuracyData[0]?.value / (accuracyData[0]?.value + accuracyData[1]?.value)) * 100).toFixed(0)}% ({accuracyData[0]?.value})
                      </div>
                      <div className="db-quiz-legend-item" style={{ color:"#767683", opacity:0.5 }}>
                        <span style={{ width:12, height:12, borderRadius:"50%", background: FAIL_COLOR, flexShrink:0 }} />
                        Failed: {((accuracyData[1]?.value / (accuracyData[0]?.value + accuracyData[1]?.value)) * 100).toFixed(0)}% ({accuracyData[1]?.value})
                      </div>
                    </div>
                    <div className="db-quiz-total">
                      {quiz_analytics?.overall_accuracy?.total_lesson_attempts || 0} Total Attempts
                    </div>
                  </div>
                ) : (
                  <div className="db-empty">
                    <div className="db-empty-icon"><FaRegQuestionCircle /></div>
                    <p>No quiz data available yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Attempt-wise Pass Distribution */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title"><FaChartLine className="coral" style={{ color:"#f96b3f" }} /> Attempt-wise Pass Distribution</h3>
              </div>
              <div className="db-card-body">
                {attemptPassData.some(d => d.value > 0) ? (
                  <>
                    <ResponsiveContainer width="100%" height={220}>
                      <BarChart data={attemptPassData} barSize={36}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e6eeff" vertical={false} />
                        <XAxis dataKey="name" tick={{ fontSize:12, fill:"#767683" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize:11, fill:"#767683" }} axisLine={false} tickLine={false} {...smartYAxis(maxAttemptVal)} />
                        <Tooltip cursor={{ fill:"rgba(0,6,102,0.04)" }} />
                        <Bar dataKey="value" name="Students Passed" radius={[6,6,0,0]}>
                          {attemptPassData.map((entry, i) => (
                            <Cell key={`c-${i}`} fill={entry.color} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="db-stat-grid">
                      <div className="db-stat-box teal">
                        <FaCheckCircle size={22} style={{ color:"#00a383" }} />
                        <p className="db-stat-box-num">{totalPassed}</p>
                        <p className="db-stat-box-lbl">Total Passed</p>
                      </div>
                      <div className="db-stat-box coral">
                        <FaTimesCircle size={22} style={{ color:"#ba1a1a" }} />
                        <p className="db-stat-box-num">{totalFailed}</p>
                        <p className="db-stat-box-lbl">Total Failed</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="db-empty">
                    <div className="db-empty-icon"><FaChartLine /></div>
                    <p>No attempt data available yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="db-analytics-grid db-mb">

            {/* Time Spent */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title"><MdTimelapse style={{ color:"#000666" }} /> Time Spent</h3>
              </div>
              <div className="db-card-body">
                <div className="db-time-grid">
                  <div className="db-time-card navy">
                    <div className="db-time-icon"><FaClock size={22} style={{ color:"#343d96" }} /></div>
                    <p className="db-time-val">{formatTime(learning_analytics?.time_spent?.total_lesson_time)}</p>
                    <p className="db-time-lbl">Lesson Time</p>
                  </div>
                  <div className="db-time-card teal">
                    <div className="db-time-icon"><FaRegQuestionCircle size={22} style={{ color:"#005048" }} /></div>
                    <p className="db-time-val">{formatTime(learning_analytics?.time_spent?.total_quiz_time)}</p>
                    <p className="db-time-lbl">Quiz Time</p>
                  </div>
                  <div className="db-time-card surface">
                    <div className="db-time-icon"><FaChartLine size={22} style={{ color:"#454652" }} /></div>
                    <p className="db-time-val">{formatTime(learning_analytics?.time_spent?.total_time_spent)}</p>
                    <p className="db-time-lbl">Total Time</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Media Usage Statistics */}
            <div className="db-card">
              <div className="db-card-header">
                <h3 className="db-card-title"><MdPermMedia style={{ color:"#f96b3f" }} /> Media Usage Statistics</h3>
              </div>
              <div className="db-card-body">
                <div className="db-musage-grid">
                  <div className="db-musage-cell navy">
                    <div className="db-musage-icon navy"><FaRegImage size={22} /></div>
                    <p className="db-musage-num">{learning_analytics?.media_usage?.total_images_viewed || 0}</p>
                    <p className="db-musage-lbl">Images View</p>
                  </div>
                  <div className="db-musage-cell teal">
                    <div className="db-musage-icon teal"><MdOutlineAudiotrack size={22} /></div>
                    <p className="db-musage-num">{learning_analytics?.media_usage?.total_audio_viewed || 0}</p>
                    <p className="db-musage-lbl">Audio Listen</p>
                  </div>
                  <div className="db-musage-cell coral">
                    <div className="db-musage-icon coral"><FaVideo size={22} /></div>
                    <p className="db-musage-num">{learning_analytics?.media_usage?.total_videos_viewed || 0}</p>
                    <p className="db-musage-lbl">Videos Play</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════
          TAB: STUDENT PERFORMANCE (school only)
      ════════════════════════════════════════════════ */}
      {activeTab === "students" && isSchool && student_performance_metrics && (
        <>
          {student_performance_metrics?.top_performing_students?.length > 0 && (
            <div className="db-card db-mb">
              <div className="db-card-header db-sh success">
                <h3 className="db-card-title"><FaTrophy /> Top Performing Students</h3>
              </div>
              <div className="db-card-body p0">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Roll No.</th>
                      <th>Email</th>
                      <th>Lessons Passed</th>
                      <th style={{ textAlign:"right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {student_performance_metrics.top_performing_students.map((student, i) => (
                      <tr key={student.id}>
                        <td style={{ color:"#767683", fontWeight:500 }}>{i + 1}</td>
                        <td>
                          <div className="db-course-cell">
                            {student.profile_pic
                              ? <img src={student.profile_pic} alt={student.name} className="db-avatar" />
                              : <div className="db-avatar-ph"><FaUserGraduate size={18} /></div>
                            }
                            <strong>{student.name}</strong>
                          </div>
                        </td>
                        <td><span className="db-pill blue">{student.roll_number || "N/A"}</span></td>
                        <td style={{ color:"#454652" }}>{student.email}</td>
                        <td><span className="db-pill green"><FaCheckCircle size={11} /> {student.total_lessons_passed || 0} Passed</span></td>
                        <td style={{ textAlign:"right" }}>
                          <button className="db-btn-outline" onClick={() => navigate(`/admin/student-detail/${student.id}`)}>
                            <FaEye size={13} /> View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {student_performance_metrics?.students_at_risk?.length > 0 && (
            <div className="db-card db-mb">
              <div className="db-card-header db-sh warn-soft">
                <h3 className="db-card-title"><FaExclamationTriangle /> Students at Risk</h3>
              </div>
              <div className="db-card-body p0">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Student</th>
                      <th>Email</th>
                      <th>Attempted</th>
                      <th>Failed</th>
                      <th>4+ Attempts</th>
                      <th>Risk Reason</th>
                      <th style={{ textAlign:"right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {student_performance_metrics.students_at_risk.map((student, i) => (
                      <tr key={student.id}>
                        <td style={{ color:"#767683", fontWeight:500 }}>{i + 1}</td>
                        <td>
                          <div className="db-course-cell">
                            {student.profile_pic
                              ? <img src={student.profile_pic} alt={student.name} className="db-avatar" />
                              : <div className="db-avatar-ph"><FaUserGraduate size={18} /></div>
                            }
                            <strong>{student.name}</strong>
                          </div>
                        </td>
                        <td style={{ color:"#454652" }}>{student.email}</td>
                        <td><span className="db-pill slate">{student.total_attempted || 0} Attempts</span></td>
                        <td><span className="db-pill red"><FaTimesCircle size={11} /> {student.total_failed || 0} Failed</span></td>
                        <td><span className="db-pill amber"><FaFire size={11} /> {student.failed_after_four || 0}</span></td>
                        <td><span className="db-pill red">{student.risk_reason || "At Risk"}</span></td>
                        <td style={{ textAlign:"right" }}>
                          <button className="db-btn-outline" onClick={() => navigate(`/admin/student-detail/${student.id}`)}>
                            <FaEye size={13} /> View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!student_performance_metrics?.top_performing_students?.length && !student_performance_metrics?.students_at_risk?.length) && (
            <div className="db-card db-mb">
              <div className="db-empty">
                <div className="db-empty-icon"><FaUserGraduate /></div>
                <p>No student performance data available yet</p>
              </div>
            </div>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════
          TAB: COURSE PERFORMANCE
      ════════════════════════════════════════════════ */}
      {activeTab === "course-performance" && (
        <>
          {course_performance_metrics?.top_used_courses?.length > 0 && (
            <div className="db-card db-mb">
              <div className="db-card-header db-sh success">
                <h3 className="db-card-title"><FaFire /> Most Used Courses (Top Performing)</h3>
              </div>
              <div className="db-card-body p0">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Course</th>
                      <th>Course ID</th>
                      <th>Lesson Attempts</th>
                      <th style={{ textAlign:"right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {course_performance_metrics.top_used_courses.map((course, i) => (
                      <tr key={course.id}>
                        <td style={{ color:"#767683", fontWeight:500 }}>{i + 1}</td>
                        <td>
                          <div className="db-course-cell">
                            {course.image
                              ? <img src={course.image} alt="" className="db-course-thumb" />
                              : <div className="db-course-thumb-ph"><FaBook size={16} /></div>
                            }
                            <strong>{course.title}</strong>
                          </div>
                        </td>
                        <td style={{ color:"#454652" }}>{course.unique_course_id}</td>
                        <td><span className="db-pill primary">{course.total_lesson_attempts} Attempts</span></td>
                        <td style={{ textAlign:"right" }}>
                          <button className="db-btn-outline" onClick={() => navigate(`/admin/course-performance/${course.id}`)}>
                            <FaEye size={13} /> View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {course_performance_metrics?.least_used_courses?.length > 0 && (
            <div className="db-card db-mb">
              <div className="db-card-header db-sh danger">
                <h3 className="db-card-title"><FaThumbsDown /> Least Used Courses (Needs Attention)</h3>
              </div>
              <div className="db-card-body p0">
                <table className="db-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Course</th>
                      <th>Course ID</th>
                      <th>Lesson Attempts</th>
                      <th style={{ textAlign:"right" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {course_performance_metrics.least_used_courses.map((course, i) => (
                      <tr key={course.id}>
                        <td style={{ color:"#767683", fontWeight:500 }}>{i + 1}</td>
                        <td>
                          <div className="db-course-cell">
                            {course.image
                              ? <img src={course.image} alt="" className="db-course-thumb" />
                              : <div className="db-course-thumb-ph"><FaBook size={16} /></div>
                            }
                            <strong>{course.title}</strong>
                          </div>
                        </td>
                        <td><span className="db-pill blue">{course.unique_course_id}</span></td>
                        <td><span className="db-pill amber">{course.total_lesson_attempts} Attempts</span></td>
                        <td style={{ textAlign:"right" }}>
                          <button className="db-btn-outline" onClick={() => navigate(`/admin/course-performance/${course.id}`)}>
                            <FaEye size={13} /> View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(!course_performance_metrics?.top_used_courses?.length && !course_performance_metrics?.least_used_courses?.length) && (
            <div className="db-card db-mb">
              <div className="db-empty">
                <div className="db-empty-icon"><FaChartLine /></div>
                <p>No course performance data available yet</p>
              </div>
            </div>
          )}
        </>
      )}

    </div>
  );
};

export default Dashboard;
